import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const PartnerWorkspaceContext = createContext(null);

export const usePartnerWorkspace = () => {
  const ctx = useContext(PartnerWorkspaceContext);
  if (!ctx) throw new Error('usePartnerWorkspace must be used within PartnerWorkspaceProvider');
  return ctx;
};

export const PartnerWorkspaceProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('pw_token'));
  const [loading, setLoading] = useState(true);
  const [navigation, setNavigation] = useState([]);

  const apiHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchMe = useCallback(async (t) => {
    const tkn = t || token;
    if (!tkn) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/partner-workspace/auth/me`, {
        headers: { 'Authorization': `Bearer ${tkn}` },
      });
      if (!res.ok) throw new Error('unauthorized');
      const data = await res.json();
      setUser(data.user);
      setOrg(data.org);
    } catch {
      setUser(null);
      setOrg(null);
      setToken(null);
      localStorage.removeItem('pw_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchNavigation = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/partner-workspace/navigation`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNavigation(data.modules || []);
      }
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchMe(); }, [fetchMe]);
  useEffect(() => { if (user) fetchNavigation(); }, [user, fetchNavigation]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/partner-workspace/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    localStorage.setItem('pw_token', data.token);
    setToken(data.token);
    setUser(data.user);
    setOrg(data.org);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pw_token');
    setToken(null);
    setUser(null);
    setOrg(null);
    setNavigation([]);
  }, []);

  const hasPermission = useCallback((perm) => {
    return user?.permissions?.includes(perm) || false;
  }, [user]);

  const switchEnvironment = useCallback(async (env) => {
    const res = await fetch(`${API}/api/partner-workspace/environment`, {
      method: 'PATCH',
      headers: apiHeaders(),
      body: JSON.stringify({ environment: env }),
    });
    if (res.ok) {
      setOrg(prev => ({ ...prev, environment: env }));
    }
  }, [apiHeaders]);

  return (
    <PartnerWorkspaceContext.Provider value={{
      user, org, token, loading, navigation,
      login, logout, hasPermission, switchEnvironment,
      apiHeaders, fetchMe, API,
    }}>
      {children}
    </PartnerWorkspaceContext.Provider>
  );
};
